import React, { useEffect, useState } from "react";
import { Button, Empty, Input } from "antd";
import { EnvironmentOutlined, PlusOutlined } from "@ant-design/icons";
import useMenu from "../hooks/useMenu";
import { nowDate } from "../components/Customs/nowDate";
import { GeneralService } from "../services/GeneralService";
import { notify } from "../components/Customs/NotifyProvider";
import ZoneTable from "../components/zone/ZoneTable";
import CreateZone from "../components/zone/CreateZone";
import {
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
} from "../utils/permissionUtils";

const MODULE_KEY = "zone";

const Zone = () => {
  const { loading, createMenu, updateMenu } = useMenu();

  const [zones, setZones] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [searchText, setSearchText] = useState("");

  const fetchZones = async () => {
    if (!canViewModule(MODULE_KEY)) return;

    try {
      const data = await GeneralService.get("zones");
      setZones(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      notify.error(error?.message || "Failed to fetch zones");
      setZones([]);
    }
  };

  useEffect(() => {
    fetchZones();
  }, []);

  if (!canViewModule(MODULE_KEY)) {
    return (
      <div className="page-shell user-master-page">
        <div className="page-empty">
          <Empty description="No permission to view zones" />
        </div>
      </div>
    );
  }

  const openCreate = () => {
    if (!canCreateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to create zone");
      return;
    }

    setEditingMenu(null);
    setDrawerOpen(true);
  };

  const openEdit = (menu) => {
    if (!canUpdateModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to update zone");
      return;
    }

    setEditingMenu(menu);
    setDrawerOpen(true);
  };

  const filteredZones = zones.filter((zone) =>
  Object.values(zone).some((val) =>
    String(val).toLowerCase().includes(searchText.toLowerCase())
  )
);

  const deleteMenu = async (id) => {
    if (!canDeleteModule(MODULE_KEY)) {
      notify.error("Permission Denied", "No permission to delete zone");
      return;
    }

    try {
      const data = await GeneralService.delete("zones", id);

      if (data?.status) {
        notify.success(data?.message || "Zone deleted successfully");
      } else {
        notify.error(data?.message || "Failed to delete zone");
      }

      fetchZones();
    } catch (error) {
      notify.error(error?.message || "Failed to delete zone");
      fetchZones();
    }
  };

  const handleSubmit = async (values) => {
    const req = {
      ...values,
      added_on: nowDate(),
      updated_on: nowDate(),
    };

    const reqUpdate = {
      ...values,
      updated_on: nowDate(),
    };

    if (editingMenu) {
      if (!canUpdateModule(MODULE_KEY)) {
        notify.error("Permission Denied", "No permission to update zone");
        return;
      }

      await updateMenu("zones", editingMenu.id, reqUpdate);
      fetchZones();
    } else {
      if (!canCreateModule(MODULE_KEY)) {
        notify.error("Permission Denied", "No permission to create zone");
        return;
      }

      await createMenu("zones", req);
      fetchZones();
    }

    setDrawerOpen(false);
  };

  return (
    <div className="page-shell user-master-page">
      <div className="user-page-header">
        <div className="user-page-header-left">
          <div className="user-page-icon">
            <EnvironmentOutlined />
          </div>

          <div>
            <h2 className="user-page-title">Manage Zone</h2>
            <p className="user-page-subtitle">
              Create, update, and manage all zones from one place.
            </p>
          </div>
        </div>

        <div
          className="user-page-header-actions"
          style={{ display: "flex", alignItems: "center" }}
        >
          <Input
            size="large"
            allowClear
            placeholder="Search zone..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 350, marginRight: 10 }}
          />

          <Button
            type="primary"
            className="primary-btn"
            icon={<PlusOutlined />}
            onClick={openCreate}
            disabled={!canCreateModule(MODULE_KEY)}
          >
            Create Zone
          </Button>
        </div>
      </div>

      <div className="page-card table-card">
        <div className="table-card-header">
          <div>
            <h3>Zone List</h3>
            <p>View and manage all available zones.</p>
          </div>
        </div>

        <ZoneTable
          data={filteredZones}
          loading={loading}
          onEdit={openEdit}
          onDelete={deleteMenu}
          canUpdate={canUpdateModule(MODULE_KEY)}
          canDelete={canDeleteModule(MODULE_KEY)}
        />
      </div>

      <CreateZone
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        initialValues={editingMenu}
      />
    </div>
  );
};

export default Zone;